import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { skipCapitalAssetMonth, unskipCapitalAssetMonth } from "slices/capitalAssets/thunk";

export type SkipModalMode = "skip" | "unskip";

interface MonthRow {
    year: number;
    month: number;       // 0-indexed
    displayMonth: number;
    isSkipped: boolean;
    isCharged: boolean;
    amount: number;
}

interface Props {
    isOpen: boolean;
    mode: SkipModalMode;
    row: MonthRow | null;
    assetId: string;
    onClose: () => void;
    onSuccess: () => void;
}

const MONTH_NAMES_ES = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const SkipMonthModal: React.FC<Props> = ({ isOpen, mode, row, assetId, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<any>();
    const { skipping } = useSelector((state: any) => state.CapitalAssets);
    const [reason, setReason] = useState("");
    const [reasonError, setReasonError] = useState("");

    const monthLabel = row ? `${MONTH_NAMES_ES[row.month]} ${row.year}` : "";

    const handleConfirm = async () => {
        if (!row) return;
        if (mode === "skip" && !reason.trim()) {
            setReasonError(t("capitalAssets.skipMonth.validation.reasonRequired"));
            return;
        }
        try {
            if (mode === "skip") {
                await dispatch(skipCapitalAssetMonth(assetId, {
                    year: row.year,
                    month: row.month + 1, // API expects 1-indexed
                    reason: reason.trim(),
                }));
            } else {
                await dispatch(unskipCapitalAssetMonth(assetId, row.year, row.month + 1));
            }
            setReason("");
            setReasonError("");
            onSuccess();
            onClose();
        } catch {
            // error handled by thunk → Redux state
        }
    };

    const handleClose = () => {
        setReason("");
        setReasonError("");
        onClose();
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered>
            <ModalHeader toggle={handleClose}>
                {mode === "skip"
                    ? t("capitalAssets.skipMonth.skipTitle")
                    : t("capitalAssets.skipMonth.unskipTitle")}
            </ModalHeader>
            <ModalBody>
                {mode === "skip" ? (
                    <>
                        <p>
                            {t("capitalAssets.skipMonth.skipBody", { month: monthLabel })}
                        </p>
                        {row && (
                            <p className="text-muted small">
                                {t("capitalAssets.skipMonth.deferredAmount")}:{" "}
                                <strong>${row.amount.toFixed(2)}</strong>
                            </p>
                        )}
                        <div className="mb-3">
                            <label className="form-label fw-semibold">
                                {t("capitalAssets.skipMonth.reasonLabel")}{" "}
                                <span className="text-danger">*</span>
                            </label>
                            <textarea
                                className={`form-control${reasonError ? " is-invalid" : ""}`}
                                rows={3}
                                value={reason}
                                onChange={(e) => { setReason(e.target.value); setReasonError(""); }}
                                placeholder={t("capitalAssets.skipMonth.reasonPlaceholder")}
                            />
                            {reasonError && <div className="invalid-feedback">{reasonError}</div>}
                        </div>
                    </>
                ) : (
                    <p>
                        {t("capitalAssets.skipMonth.unskipBody", { month: monthLabel })}
                    </p>
                )}
            </ModalBody>
            <ModalFooter>
                <Button className="farm-secondary-button" onClick={handleClose} disabled={skipping}>
                    {t("common.button.cancel")}
                </Button>
                <Button
                    color={mode === "skip" ? "warning" : "danger"}
                    onClick={handleConfirm}
                    disabled={skipping}
                >
                    {skipping ? <Spinner size="sm" /> : t("common.button.confirm")}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default SkipMonthModal;
